<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['addNode']);
const { t } = useI18n();

const groups = computed(() => [
  {
    key: 'messages',
    title: t('FLOWS.PALETTE.MESSAGES'),
    items: [
      {
        kind: 'send_text',
        icon: 'i-lucide-message-square',
        title: t('FLOWS.NODES.SEND_TEXT.TITLE'),
        description: t('FLOWS.NODES.SEND_TEXT.DESCRIPTION'),
      },
    ],
  },
  {
    key: 'logic',
    title: t('FLOWS.PALETTE.LOGIC'),
    items: [
      {
        kind: 'wait',
        icon: 'i-lucide-clock-3',
        title: t('FLOWS.NODES.WAIT.TITLE'),
        description: t('FLOWS.NODES.WAIT.DESCRIPTION'),
      },
      {
        kind: 'condition',
        icon: 'i-lucide-git-branch',
        title: t('FLOWS.NODES.CONDITION.TITLE'),
        description: t('FLOWS.NODES.CONDITION.DESCRIPTION'),
      },
      {
        kind: 'switch',
        icon: 'i-lucide-git-fork',
        title: t('FLOWS.NODES.SWITCH.TITLE'),
        description: t('FLOWS.NODES.SWITCH.DESCRIPTION'),
      },
    ],
  },
  {
    key: 'actions',
    title: t('FLOWS.PALETTE.ACTIONS'),
    items: [
      {
        kind: 'capture_input',
        icon: 'i-lucide-message-circle-question',
        title: t('FLOWS.NODES.CAPTURE_INPUT.TITLE'),
        description: t('FLOWS.NODES.CAPTURE_INPUT.DESCRIPTION'),
      },
      {
        kind: 'assign_agent',
        icon: 'i-lucide-user-check',
        title: t('FLOWS.NODES.ASSIGN_AGENT.TITLE'),
        description: t('FLOWS.NODES.ASSIGN_AGENT.DESCRIPTION'),
      },
      {
        kind: 'set_status',
        icon: 'i-lucide-circle-dot',
        title: t('FLOWS.NODES.SET_STATUS.TITLE'),
        description: t('FLOWS.NODES.SET_STATUS.DESCRIPTION'),
      },
      {
        kind: 'set_labels',
        icon: 'i-lucide-tags',
        title: t('FLOWS.NODES.SET_LABELS.TITLE'),
        description: t('FLOWS.NODES.SET_LABELS.DESCRIPTION'),
      },
    ],
  },
  {
    key: 'integrations',
    title: t('FLOWS.PALETTE.INTEGRATIONS'),
    items: [
      {
        kind: 'ai_agent',
        icon: 'i-lucide-sparkles',
        title: t('FLOWS.NODES.AI_AGENT.TITLE'),
        description: t('FLOWS.NODES.AI_AGENT.DESCRIPTION'),
      },
      {
        kind: 'http_request',
        icon: 'i-lucide-globe',
        title: t('FLOWS.NODES.HTTP_REQUEST.TITLE'),
        description: t('FLOWS.NODES.HTTP_REQUEST.DESCRIPTION'),
      },
    ],
  },
]);

const addNode = item => {
  emit('addNode', item.kind);
};
</script>

<template>
  <aside
    class="flex flex-col flex-none w-64 min-h-0 border-e border-n-weak bg-n-solid-1"
  >
    <div class="px-4 pt-4 pb-3 border-b border-n-weak">
      <p
        class="text-xs font-semibold uppercase tracking-[0.14em] text-n-slate-10"
      >
        {{ t('FLOWS.PALETTE.TITLE') }}
      </p>
      <p class="mt-1 text-xs leading-5 text-n-slate-11">
        {{ t('FLOWS.PALETTE.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto p-3">
      <section v-for="group in groups" :key="group.key" class="mb-5 last:mb-0">
        <h3
          class="px-2 mb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-n-slate-10"
        >
          {{ group.title }}
        </h3>
        <div class="flex flex-col gap-2">
          <button
            v-for="item in group.items"
            :key="item.kind"
            type="button"
            class="flex items-start gap-3 p-3 text-start border rounded-xl transition-colors border-n-weak bg-n-background hover:border-n-brand hover:bg-n-brand/5"
            @click="addNode(item)"
          >
            <span
              class="flex items-center justify-center flex-none w-8 h-8 rounded-lg bg-n-alpha-black1"
            >
              <span class="text-base text-n-slate-11" :class="item.icon" />
            </span>
            <span class="min-w-0">
              <span
                class="flex items-center gap-2 text-xs font-semibold text-n-slate-12"
              >
                {{ item.title }}
              </span>
              <span
                class="block mt-1 text-[0.6875rem] leading-4 text-n-slate-10"
              >
                {{ item.description }}
              </span>
            </span>
          </button>
        </div>
      </section>
    </div>
  </aside>
</template>
